// client/src/pages/Reports.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { listMyReports, getReportDownloadUrl } from '../tmApi';
import { LoadingBar, LoadingInline, LoadingUiStyles } from './LoadingUi';

const th = { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e2e8f0', fontSize: 13, color: '#334155', background: '#f8fafc' };
const td = { padding: '10px 8px', borderBottom: '1px solid #e2e8f0', fontSize: 14 };
const btn = { padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#e0f2fe', cursor: 'pointer' };

function fmtINR(n) {
  if (n == null) return '-';
  try { return Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  catch { return n; }
}

export default function Reports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({});

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await listMyReports();
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter(r =>
      (r.slug || '').toLowerCase().includes(s) ||
      (r.dataset || '').toLowerCase().includes(s) ||
      (r.fileName || '').toLowerCase().includes(s)
    );
  }, [items, search]);

  async function getLink(purchaseId) {
    try {
      setBusy(x => ({ ...x, [purchaseId]: true }));
      const d = await getReportDownloadUrl(purchaseId); // 1-hour link
      if (d?.url) window.open(d.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      alert('Could not generate a download link. Please try again.');
    } finally {
      setBusy(x => ({ ...x, [purchaseId]: false }));
    }
  }

  return (
    <Layout>
      <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 12px' }}>
        <LoadingUiStyles />
        <h1>My Reports</h1>
        <p style={{ marginBottom: 12 }}>
          All reports you've purchased. Links are temporary (valid for 1 hour). Click "Get link" anytime to generate a fresh one.
        </p>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by slug/dataset/filename..."
          style={{ width: '100%', maxWidth: 420, padding: 10, border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 12 }}
        />

        <LoadingBar loading={loading} />

        {loading ? <LoadingInline loading={loading} text="Loading reports... please wait." /> : filtered.length === 0 ? 'No reports yet.' : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={th}>Purchased</th>
                  <th style={th}>Dataset</th>
                  <th style={th}>Slug</th>
                  <th style={th}>Period</th>
                  <th style={{ ...th, textAlign: 'right' }}>Rows</th>
                  <th style={{ ...th, textAlign: 'right' }}>Price</th>
                  <th style={th}>Status</th>
                  <th style={th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const when = r.purchasedAt ? new Date(r.purchasedAt).toLocaleString() : '-';
                  const currency = String(r.currency || 'INR').toUpperCase();
                  const price = (r.amountRupees != null)
                    ? (currency === 'INR' ? `Rs ${fmtINR(r.amountRupees)}` : `${currency} ${fmtINR(r.amountRupees)}`)
                    : '-';
                  return (
                    <tr key={r.purchaseId}>
                      <td style={td}>{when}</td>
                      <td style={td}>{r.dataset}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{r.slug}</div>
                        {r?.buyer?.phoneNumber && (
                          <div style={{ fontSize: 12, color: '#475569' }}>Phone: {r.buyer.phoneNumber}</div>
                        )}
                      </td>
                      <td style={td}>{`${r.dateFrom} -> ${r.dateTo}`}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{r.rows?.toLocaleString?.() ?? '-'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{price}</td>
                      <td style={td}>{(r.status || 'ready').toUpperCase()}</td>
                      <td style={td}>
                        <button
                          onClick={() => getLink(r.purchaseId)}
                          disabled={busy[r.purchaseId] || r.status !== 'ready'}
                          style={btn}
                          title="Generate a fresh 1-hour link"
                        >
                          {busy[r.purchaseId] ? 'Generating...' : 'Get link'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
              If a link expires, click "Get link" again to create a new one.
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
